'use client';

import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { db, requireDb } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { getCache, setCache } from '@/lib/cache';
import { Article } from '@/types/article';
import { useDarkMode } from '@/lib/DarkModeContext';
import { getArticleUrl } from '@/lib/slugs';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

export interface CategoryPageClientProps {
    /** Main category name (e.g., "Municija", "Naoružanje") */
    kategorija: string;
    /** Subcategory name (e.g., "Ručne bombe", "Kopnene snage") */
    podkategorija: string;
    /** Optional sub-subcategory for filtering */
    podpodkategorija?: string;
    /** Display title for the page */
    title: string;
    /** Breadcrumb path (e.g., "Municija › Ručne bombe") */
    breadcrumb: string;
    /** Section header text (e.g., "NAJNOVIJE U RUČNE BOMBE") */
    sectionTitle: string;
}

export default function CategoryPageClient({
    kategorija,
    podkategorija,
    podpodkategorija,
    title,
    breadcrumb,
    sectionTitle
}: CategoryPageClientProps) {
    // Read ?podkategorija= and ?podpodkategorija= reliably using Next's navigation hook
    const searchParams = useSearchParams();
    const queryPodkategorija = searchParams ? (searchParams.get('podkategorija') || '') : '';
    const queryPodpodkategorija = searchParams ? (searchParams.get('podpodkategorija') || '') : '';

    // Prefer explicit prop, otherwise read from URL query '?podkategorija=' (used for client-side filtering)
    const activePodkategorija = podkategorija || queryPodkategorija;
    const activePodpodkategorija = podpodkategorija || queryPodpodkategorija;

    const router = useRouter();

    const getCategoryBasePath = (kat: string) => {
        const k = (kat || '').toString().trim().toLowerCase();
        switch (k) {
            case 'naoružanje':
            case 'naoruzanje':
                return '/articles/weapons';
            case 'municija':
            case 'municijа':
                return '/articles/ammunition';
            case 'analitika':
                return '/articles/analytics';
            case 'namjenska preduzeća':
            case 'namjenska preduzeca':
            case 'namjenska preduzeća':
                return '/articles/enterprises';
            case 'istorija':
            case 'vojna istorija':
                // History has a dedicated base under /articles/history
                return '/articles/history';
            case 'vijesti':
                return '/svi-clanci';
            default:
                return '/articles';
        }
    };

    const handlePodClick = (sub: string) => {
        const base = getCategoryBasePath(kategorija || '');

        if (activePodkategorija === sub) {
            // deselect -> show all
            router.push(base);
        } else {
            router.push(`${base}?podkategorija=${encodeURIComponent(sub)}`);
        }
    };

    const handlePodpodClick = (sub: string) => {
        const base = getCategoryBasePath(kategorija || '');
        if (activePodpodkategorija === sub) {
            // deselect podpod and keep podkategorija
            router.push(`${base}?podkategorija=${encodeURIComponent(activePodkategorija)}`);
        } else {
            router.push(`${base}?podkategorija=${encodeURIComponent(activePodkategorija)}&podpodkategorija=${encodeURIComponent(sub)}`);
        }
    };

    const { darkMode } = useDarkMode();
    const { siteLang } = useSiteLanguage();

    const categoryTranslations: Record<string, string> = {
        'Vijesti': 'News',
        'Naoru\u017eanje': 'Armament', 'Municija': 'Ammunition',
        'Namjenska preduze\u0107a': 'Defense Enterprises', 'Vojna istorija': 'Military History',
        'Kopnene snage': 'Ground Forces', 'Vazduhoplovstvo i PVO': 'Air Force & Air Defense',
        'Mornarica': 'Navy', 'Strate\u0161ke snage': 'Strategic Forces',
        'Naoru\u017eanje Tre\u0107eg rajha': 'Third Reich Armaments', 'Naoru\u017eanje biv\u0161e JNA': 'Former JNA Armaments',
        'Savremeno naoru\u017eanje': 'Modern Armaments', 'Konstrukcija naoru\u017eanja': 'Weapon Construction',
        'Tenkovi i oklopna vozila': 'Tanks & Armored Vehicles', 'Artiljerija': 'Artillery',
        'Samostreli': 'Crossbows', 'Pi\u0161tolji': 'Pistols', 'Revolveri': 'Revolvers',
        'Automati': 'Submachine Guns', 'Pu\u0161ke': 'Rifles', 'Karabini': 'Carbines',
        'Pu\u0161komitraljezi': 'Light Machine Guns', 'Mitraljezi': 'Machine Guns',
        'Ru\u010dni baca\u010di': 'Hand Launchers', 'Ru\u010dni raketni baca\u010di': 'Rocket Launchers',
        'Baca\u010di granata': 'Grenade Launchers', 'Trombloni': 'Rifle Grenade Launchers',
        'Avioni i helikopteri': 'Aircraft & Helicopters', 'PVO sistemi i raketno naoru\u017eanje': 'Air Defense & Missile Systems',
        'Bespilotne letjelice': 'Unmanned Aerial Vehicles (UAV)',
        'Površinska plovila': 'Surface Vessels', 'Podmornica': 'Submarines',
        'Mornarničko naoružanje': 'Naval Weapons', 'Mornaricko naoružanje': 'Naval Weapons', 'Mornaričko naoružanje': 'Naval Weapons',
        'Pje\u0161adijska municija': 'Infantry Ammunition', 'Minobaca\u010dka municija': 'Mortar Ammunition',
        'Klasi\u010dna artiljerijska municija': 'Classic Artillery Ammunition',
        'Raketna artiljerijska municija': 'Rocket Artillery Ammunition',
        'Vodjene rakete': 'Guided Missiles', 'Ru\u010dne bombe': 'Hand Grenades',
        'Mine za ru\u010dne baca\u010de': 'Grenades for Handheld Launchers',
        'Municija za bestrzajne topove': 'Recoilless Cannon Ammunition',
        'Balistika': 'Ballistics', 'Savremena municija': 'Modern Ammunition',
        'Eksplozivne materije': 'Explosives', 'Elementi municije': 'Ammunition Elements',
        'Pirotehnika': 'Pyrotechnics', 'Upalja\u010di': 'Fuzes', 'Upalja\u010dki': 'Fuzes',
        'Istorija razvoja': 'History of Development', 'Proizvodni kapaciteti': 'Production Capacity',
        'Stari jugoslovenski projekti': 'Old Yugoslav Projects',
        'Modernizacija i izvozni program': 'Modernization & Export Program',
        'Zemlje biv\u0161e Jugoslavije': 'Former Yugoslav Countries',
        'Evropa': 'Europe', 'NATO': 'NATO', 'Rusija': 'Russia',
        'Bliski istok': 'Middle East', 'Rat u Ukrajini': 'War in Ukraine',
        'Vojske kroz vijekove': 'Armies Through the Ages',
        'I svjetski rat': 'World War I', 'II svjetski rat': 'World War II',
        'Hladni rat': 'Cold War', 'Poznati konstruktori': 'Famous Designers',
        'Nekategorisano': 'Uncategorized',
        'Strateško naoružanje': 'Strategic Armaments',
    };

    // Display name overrides (SR rename, applied before EN translation)
    const displayRenames: Record<string, string> = {
        'Strateške snage': 'Strateško naoružanje',
    };

    const translateCat = (name: string) => {
        const display = displayRenames[name] ?? name;
        return siteLang === 'en' ? (categoryTranslations[display] ?? categoryTranslations[name] ?? display) : display;
    };

    const [articles, setArticles] = useState<Article[]>([]);
    const [loading, setLoading] = useState(true);
    const [subcategories, setSubcategories] = useState<string[]>([]);

    // Filter by language
    const displayedArticles = articles.filter(a =>
        siteLang === 'en' ? a.lang === 'en' : (a.lang === 'sr' || !a.lang)
    );

    useEffect(() => {
        const loadArticles = async () => {
            const TTL = 10 * 60 * 1000; // 10 minutes
            const effectivePodkategorija = podkategorija || queryPodkategorija;
            const effectivePodpodkategorija = podpodkategorija || queryPodpodkategorija;
            const cacheKey = `category:${kategorija}:${effectivePodkategorija || ''}:${effectivePodpodkategorija || ''}`;

            const normalize = (s?: string) => (s || '').toString().trim().toLowerCase();
            const effectivePodkategorijaNorm = normalize(effectivePodkategorija);
            const effectivePodpodkategorijaNorm = normalize(effectivePodpodkategorija);

            // Match categories, treating 'Istorija' and 'Vojna istorija' as equivalent to avoid mismatches
            const matchesCategory = (articleCategory?: string) => {
                const a = normalize(articleCategory);
                const k = normalize(kategorija);
                if (a === k) return true;
                if ((a === 'vojna istorija' && k === 'istorija') || (a === 'istorija' && k === 'vojna istorija')) return true;
                return false;
            };

            // Try to read from cache synchronously
            const cached = getCache<Article[]>(cacheKey);
            if (cached) {
                setArticles(cached);

                // DEV DEBUG: print cached podkategorija values for troubleshooting (remove in prod)
                if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
                    try {
                        console.debug('[CategoryPageClient DEBUG] effectivePodkategorija:', effectivePodkategorija);
                        console.debug('[CategoryPageClient DEBUG] cached podkategorije:', Array.from(new Set(cached.map(a => a.podkategorija))));
                        console.debug('[CategoryPageClient DEBUG] normalized cached podkategorije:', Array.from(new Set(cached.map(a => (a.podkategorija || '').toString().trim().toLowerCase()))));
                    } catch (e) { }
                }

                // derive subcategories from cached data
                let uniqueSubcategories: string[] = [];
                if (effectivePodkategorija) {
                    const categoryArticles = cached.filter((article) =>
                        matchesCategory(article.kategorija) && normalize(article.podkategorija) === effectivePodkategorijaNorm
                    );
                    uniqueSubcategories = Array.from(new Set(categoryArticles.map(a => a.podpodkategorija).filter(Boolean))).sort();
                } else {
                    const categoryArticles = cached.filter((article) => matchesCategory(article.kategorija));
                    uniqueSubcategories = Array.from(new Set(categoryArticles.map(a => a.podkategorija).filter(Boolean))).sort();
                }
                // Ensure Naoružanje, Municija, Analitika, Namjenska preduzeća and Vojna istorija show all known podkategorije even if some have no articles
                const weaponsSubcategories = ['Vazduhoplovstvo i PVO', 'Kopnene snage', 'Mornarica', 'Strateške snage', 'Naoružanje Trećeg rajha', 'Naoružanje bivše JNA', 'Savremeno naoružanje', 'Konstrukcija naoružanja', 'Nekategorisano'];
            const ammunitionSubcategories = ['Pješadijska municija', 'Minobacačka municija', 'Klasična artiljerijska municija', 'Raketna artiljerijska municija', 'Vodjene rakete', 'Ručne bombe', 'Mine za ručne bacače', 'Ručni raketni bacači', 'Municija za bestrzajne topove', 'Balistika', 'Savremena municija', 'Eksplozivne materije', 'Elementi municije', 'Pirotehnika', 'Upaljački', 'Nekategorisano'];
                const analyticsSubcategories = ['Zemlje bivše Jugoslavije', 'Evropa', 'NATO', 'Rusija', 'Bliski istok', 'Rat u Ukrajini', 'Nekategorisano'];
                const enterprisesSubcategories = ['Istorija razvoja', 'Proizvodni kapaciteti', 'Stari jugoslovenski projekti', 'Modernizacija i izvozni program', 'Nekategorisano'];
                const historySubcategories = ['Vojske kroz vijekove', 'I svjetski rat', 'II svjetski rat', 'Hladni rat', 'Poznati konstruktori', 'Nekategorisano'];
                if ((kategorija === 'Naoružanje' || kategorija === 'Municija' || kategorija === 'Analitika' || kategorija === 'Namjenska preduzeća' || kategorija === 'Vojna istorija') && !effectivePodkategorija) {
                    let base = weaponsSubcategories;
                    if (kategorija === 'Municija') base = ammunitionSubcategories;
                    if (kategorija === 'Analitika') base = analyticsSubcategories;
                    if (kategorija === 'Namjenska preduzeća') base = enterprisesSubcategories;
                    if (kategorija === 'Vojna istorija') base = historySubcategories;
                    const merged = Array.from(new Set([...base, ...uniqueSubcategories]));
                    setSubcategories(merged);
                } else {
                    setSubcategories(uniqueSubcategories);
                }
                setLoading(false);

                // Background refresh to update cache if newer data exists
                (async () => {
                    try {
                        if (!db) return; // Firestore not initialized; skip background refresh in non-browser or uninitialized envs
                        const querySnapshot = await getDocs(collection(requireDb(), 'clanci'));
                        const allArticles: Article[] = [];
                        querySnapshot.docs.forEach((doc) => {
                            allArticles.push({
                                ...doc.data(),
                                docId: doc.id
                            } as Article);
                        });

                        const filtered = allArticles.filter((article) => {
                            const matchesKategorija = matchesCategory(article.kategorija);
                            // If a specific podkategorija is provided, require a match.
                            // If not (top-level category page), include all articles in the category (important for 'Vijesti').
                            const matchesPodkategorija = effectivePodkategorija ? normalize(article.podkategorija) === effectivePodkategorijaNorm : true;
                            const matchesPodpodkategorija = effectivePodpodkategorija ? normalize(article.podpodkategorija) === effectivePodpodkategorijaNorm : true;

                            return matchesKategorija && matchesPodkategorija && matchesPodpodkategorija;
                        });

                        filtered.sort((a, b) => (b.id || 0) - (a.id || 0));

                        // DEV DEBUG: show filtered counts and sample of podkategorije
                        if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
                            try {
                                console.debug('[CategoryPageClient DEBUG] background filtered count:', filtered.length);
                                console.debug('[CategoryPageClient DEBUG] background filtered podkategorije:', Array.from(new Set(filtered.map(a => a.podkategorija))));
                                console.debug('[CategoryPageClient DEBUG] sample filtered normalized:', (filtered.slice(0, 5).map(a => ({ id: a.id, podkategorija: a.podkategorija, podkategorijaNorm: (a.podkategorija || '').toString().trim().toLowerCase() }))));
                            } catch (e) { }
                        }

                        setCache(cacheKey, filtered, TTL);

                        if (JSON.stringify(filtered) !== JSON.stringify(cached)) {
                            setArticles(filtered);

                            let uniqueSubcategories2: string[] = [];
                            if (effectivePodkategorija) {
                                const categoryArticles2 = filtered.filter((article) =>
                                    matchesCategory(article.kategorija) && normalize(article.podkategorija) === effectivePodkategorijaNorm
                                );
                                uniqueSubcategories2 = Array.from(new Set(categoryArticles2.map(a => a.podpodkategorija).filter(Boolean))).sort();
                            } else {
                                const categoryArticles2 = filtered.filter((article) => matchesCategory(article.kategorija));
                                uniqueSubcategories2 = Array.from(new Set(categoryArticles2.map(a => a.podkategorija).filter(Boolean))).sort();
                            }
                            // Ensure Naoružanje, Municija, Analitika and Namjenska preduzeća show all known podkategorije even if some have no articles
                            const weaponsSubcategories = ['Vazduhoplovstvo i PVO', 'Kopnene snage', 'Mornarica', 'Strateške snage', 'Naoružanje Trećeg rajha', 'Naoružanje bivše JNA', 'Savremeno naoružanje', 'Konstrukcija naoružanja', 'Nekategorisano'];
                            const ammunitionSubcategories = ['Pješadijska municija', 'Minobacačka municija', 'Klasična artiljerijska municija', 'Raketna artiljerijska municija', 'Vodjene rakete', 'Ručne bombe', 'Mine za ručne bacače', 'Ručni raketni bacači', 'Municija za bestrzajne topove', 'Balistika', 'Savremena municija', 'Eksplozivne materije', 'Elementi municije', 'Pirotehnika', 'Upaljači', 'Nekategorisano'];
                            const analyticsSubcategories = ['Zemlje bivše Jugoslavije', 'Evropa', 'NATO', 'Rusija', 'Bliski istok', 'Rat u Ukrajini', 'Nekategorisano'];
                            const enterprisesSubcategories = ['Istorija razvoja', 'Proizvodni kapaciteti', 'Stari jugoslovenski projekti', 'Modernizacija i izvozni program', 'Nekategorisano'];
                            const historySubcategories = ['Vojske kroz vijekove', 'I svjetski rat', 'II svjetski rat', 'Hladni rat', 'Poznati konstruktori', 'Nekategorisano'];
                            if ((kategorija === 'Naoružanje' || kategorija === 'Municija' || kategorija === 'Analitika' || kategorija === 'Namjenska preduzeća' || kategorija === 'Vojna istorija') && !effectivePodkategorija) {
                                const base2 = kategorija === 'Naoružanje' ? weaponsSubcategories : (kategorija === 'Municija' ? ammunitionSubcategories : (kategorija === 'Analitika' ? analyticsSubcategories : (kategorija === 'Namjenska preduzeća' ? enterprisesSubcategories : historySubcategories)));
                                const merged2 = Array.from(new Set([...base2, ...uniqueSubcategories2]));
                                setSubcategories(merged2);
                            } else {
                                setSubcategories(uniqueSubcategories2);
                            }
                        }
                    } catch {
                        // background refresh error
                    }
                })();

                return;
            }

            // No cache — fetch and populate
            try {
                if (!db) { // Firestore not initialized; bail out gracefully
                    setLoading(false);
                    return;
                }
                const querySnapshot = await getDocs(collection(requireDb(), 'clanci'));

                const allArticles: Article[] = [];
                querySnapshot.docs.forEach((doc) => {
                    allArticles.push({
                        ...doc.data(),
                        docId: doc.id
                    } as Article);
                });

                // DEV DEBUG: print fetched podkategorija values (remove in prod)
                if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
                    try {
                        console.debug('[CategoryPageClient DEBUG] effectivePodkategorija:', effectivePodkategorija);
                        console.debug('[CategoryPageClient DEBUG] fetched podkategorije:', Array.from(new Set(allArticles.map(a => a.podkategorija))));
                        console.debug('[CategoryPageClient DEBUG] normalized fetched podkategorije:', Array.from(new Set(allArticles.map(a => (a.podkategorija || '').toString().trim().toLowerCase()))));
                    } catch (e) { }
                }

                // Filter by category
                const filtered = allArticles.filter((article) => {
                    const matchesKategorija = matchesCategory(article.kategorija);
                    // If a specific podkategorija is provided, require a match.
                    // If not (top-level category page), include all articles in the category (important for 'Vijesti').
                    const matchesPodkategorija = effectivePodkategorija ? normalize(article.podkategorija) === effectivePodkategorijaNorm : true;
                    const matchesPodpodkategorija = effectivePodpodkategorija ? normalize(article.podpodkategorija) === effectivePodpodkategorijaNorm : true;

                    return matchesKategorija && matchesPodkategorija && matchesPodpodkategorija;
                });

                // Sort by ID (newest first)
                filtered.sort((a, b) => (b.id || 0) - (a.id || 0));

                if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
                    try {
                        console.debug('[CategoryPageClient DEBUG] initial filtered count:', filtered.length);
                        console.debug('[CategoryPageClient DEBUG] initial filtered podkategorije:', Array.from(new Set(filtered.map(a => a.podkategorija))));
                    } catch (e) { }
                }

                setArticles(filtered);

                // Get unique subcategories for the category and subcategory (or top-level subcategories for category pages)
                let uniqueSubcategories: string[] = [];
                if (effectivePodkategorija) {
                    const categoryArticles = allArticles.filter((article) =>
                        matchesCategory(article.kategorija) && normalize(article.podkategorija) === effectivePodkategorijaNorm
                    );
                    uniqueSubcategories = Array.from(new Set(categoryArticles.map(a => a.podpodkategorija).filter(Boolean))).sort();
                } else {
                    const categoryArticles = allArticles.filter((article) => matchesCategory(article.kategorija));
                    uniqueSubcategories = Array.from(new Set(categoryArticles.map(a => a.podkategorija).filter(Boolean))).sort();
                }
                // Ensure Naoružanje, Municija, Analitika and Namjenska preduzeća show all known podkategorije even if some have no articles
                const weaponsSubcategories = ['Vazduhoplovstvo i PVO', 'Kopnene snage', 'Mornarica', 'Strateške snage', 'Naoružanje Trećeg rajha', 'Naoružanje bivše JNA', 'Savremeno naoružanje', 'Konstrukcija naoružanja', 'Nekategorisano'];
                const ammunitionSubcategories = ['Pješadijska municija', 'Minobacačka municija', 'Artiljerijska municija', 'Raketna artiljerijska municija', 'Vodjene rakete', 'Ručne bombe', 'Mine za ručne bacače', 'Ručni reketni bacački', 'Municija za bestrzajne topove', 'Balistika', 'Savremena municija', 'Eksplozivne materije', 'Nekategorisano'];
                const analyticsSubcategories = ['Zemlje bivše Jugoslavije', 'Evropa', 'NATO', 'Rusija', 'Bliski istok', 'Rat u Ukrajini', 'Nekategorisano'];
                const enterprisesSubcategories = ['Istorija razvoja', 'Proizvodni kapaciteti', 'Stari jugoslovenski projekti', 'Modernizacija i izvozni program', 'Nekategorisano'];
                if ((kategorija === 'Naoružanje' || kategorija === 'Municija' || kategorija === 'Analitika' || kategorija === 'Namjenska preduzeća') && !effectivePodkategorija) {
                    const base = kategorija === 'Naoružanje' ? weaponsSubcategories : (kategorija === 'Municija' ? ammunitionSubcategories : (kategorija === 'Analitika' ? analyticsSubcategories : enterprisesSubcategories));
                    const merged = Array.from(new Set([...base, ...uniqueSubcategories]));
                    setSubcategories(merged);
                } else {
                    setSubcategories(uniqueSubcategories);
                }

                // Cache it
                setCache(cacheKey, filtered, TTL);
            } catch {
                // Error loading articles
            } finally {
                setLoading(false);
            }
        };

        loadArticles();
    }, [kategorija, podkategorija, podpodkategorija, queryPodkategorija, queryPodpodkategorija]);

    const noHeaderPadding = (sectionTitle && sectionTitle.trim() === 'Avioni i helikopteri') || (breadcrumb && breadcrumb.includes('Avioni i helikopteri')) || (sectionTitle && sectionTitle.trim() === 'Podmornica') || (breadcrumb && breadcrumb.includes('Podmornica'));
    const isDronesSection = sectionTitle && sectionTitle.trim() === 'NAJNOVIJE U BESPILOTNE LETJELICE';
    const headerInnerClass = displayedArticles.length === 0 && subcategories.length > 0
        ? 'h-auto flex-col'
        : (isDronesSection ? 'h-[40px] md:h-[40px] flex items-center' : 'h-[40px] md:h-auto flex items-center');

    // Prefer the provided section title.
    // If none provided: use 'Najnovije vijesti' only for the 'Vijesti' category;
    // otherwise construct a specific fallback 'NAJNOVIJE U <label>' using the most specific category available
    const headerLabel = (podpodkategorija || podkategorija || kategorija || '').toString().trim();
    const headerTitle = siteLang === 'en'
        ? (kategorija?.toString().trim().toLowerCase() === 'vijesti'
            ? 'LATEST NEWS'
            : `LATEST IN ${translateCat(headerLabel).toUpperCase()}`)
        : (sectionTitle || (kategorija && kategorija.toString().trim().toLowerCase() === 'vijesti'
            ? 'Najnovije vijesti'
            : `NAJNOVIJE U ${headerLabel.toUpperCase()}`));

    // Helper to map Naoružanje subcategory names to their URL slugs
    const getWeaponsSubcategoryPath = (sub: string) => {
        switch ((sub || '').toLowerCase()) {
            case 'kopnene snage': return '/articles/weapons/army';
            case 'vazduhoplovstvo i pvo': return '/articles/weapons/airforce';
            case 'mornarica': return '/articles/weapons/navy';
            case 'strateške snage': return '/articles/weapons/strategic';
            case 'savremeno naoružanje': return '/articles/weapons/modern';
            case 'konstrukcija naoružanja': return '/articles/weapons/konstrukcija';
            case 'nekategorisano': return '/articles/weapons';
            default: return '/articles';
        }
    };

    // Known pod-podkategorije for Naoružanje subcategories (used to show options even if no articles exist)
    const weaponsPodpodMap: Record<string, string[]> = {
        'Kopnene snage': ['Tenkovi i oklopna vozila', 'Artiljerija', 'Samostreli', 'Pištolji', 'Revolveri', 'Automati', 'Puške', 'Karabini', 'Puškomitraljezi', 'Mitraljezi', 'Ručni bacači', 'Ručni raketni bacači', 'Bacači granata', 'Trombloni'],
        'Vazduhoplovstvo i PVO': ['Avioni i helikopteri', 'PVO sistemi i raketno naoružanje', 'Bespilotne letjelice'],
        'Mornarica': ['Površinska plovila', 'Podmornica', 'Mornaričko naoružanje']
    };

    const normalizeLocal = (s?: string) => (s || '').toString().trim();

    // Derived pod-podkategorije for the currently active podkategorija (merge known + discovered from articles)
    const podpodkategorijeForActive = activePodkategorija ? (() => {
        const known = weaponsPodpodMap[activePodkategorija] || [];
        const fromArticles = Array.from(new Set(articles
            .filter(a => normalizeLocal(a.podkategorija).toLowerCase() === normalizeLocal(activePodkategorija).toLowerCase())
            .map(a => a.podpodkategorija).filter(Boolean)
        )) as string[];
        // merge preserving known order first, then appended discovered ones
        const merged = Array.from(new Set([...known, ...fromArticles]));
        return merged;
    })() : [];

    // Add conditional spacing class for Naoružanje so pod-podkategorije are visually separated
    const podpodContainerClass = (kategorija && kategorija.toString().trim().toLowerCase() === 'naoružanje')
        ? 'mt-6 md:mt-8 flex flex-wrap justify-center gap-3'
        : 'mt-2 flex flex-wrap justify-center gap-2';

    // Quick links data for multiple categories (used in the header area)
    const categoryQuickLinks: Record<string, string[]> = {
        'naoružanje': ['Kopnene snage', 'Vazduhoplovstvo i PVO', 'Mornarica', 'Strateške snage', 'Naoružanje Trećeg rajha', 'Naoružanje bivše JNA', 'Savremeno naoružanje', 'Konstrukcija naoružanja', 'Nekategorisano'],
        'municija': ['Pješadijska municija', 'Minobacačka municija', 'Klasična artiljerijska municija', 'Raketna artiljerijska municija', 'Vodjene rakete', 'Ručne bombe', 'Mine za ručne bacače', 'Ručni raketni bacači', 'Municija za bestrzajne topove', 'Balistika', 'Savremena municija', 'Eksplozivne materije', 'Elementi municije', 'Pirotehnika', 'Upaljači', 'Nekategorisano'],
        'analitika': ['Zemlje bivše Jugoslavije', 'Evropa', 'NATO', 'Rusija', 'Bliski istok', 'Rat u Ukrajini', 'Nekategorisano'],
        'namjenska preduzeća': ['Istorija razvoja', 'Proizvodni kapaciteti', 'Stari jugoslovenski projekti', 'Modernizacija i izvozni program', 'Nekategorisano'],
        'vojna istorija': ['Vojske kroz vijekove', 'I svjetski rat', 'II svjetski rat', 'Hladni rat', 'Poznati konstruktori', 'Nekategorisano'],
    };

    const katKey = (kategorija || '').toString().trim().toLowerCase();
    const quickItems = categoryQuickLinks[katKey] || [];

    return (
        <main className={`min-h-screen no-top-gap ${darkMode ? 'bg-gray-900' : 'bg-white'} flex flex-col`}>
            <Header />

            {/* Hero Section */}
            <section className="mt-0 -mt-16 bg-gray-900 pt-4 md:pt-20 pb-4 md:pb-12 px-0 lg:px-[calc(1rem+180px)]" style={{ backgroundColor: "#111827" }}>
                <div className="md:max-w-5xl md:mx-auto text-center">
                    {/**
                     * Breadcrumb behavior:
                     * - If breadcrumb is just 'Naoružanje' -> don't show the breadcrumb line
                     * - If breadcrumb starts with 'Naoružanje' and has more parts, skip the first part
                     */}
                    {(() => {
                        const parts = breadcrumb.split('›').map(p => p.trim()).filter(Boolean);
                        const showParts = (parts.length === 1 && (parts[0].toLowerCase() === 'naoružanje' || parts[0].toLowerCase() === 'municija' || parts[0].toLowerCase() === 'analitika' || parts[0].toLowerCase() === 'namjenska preduzeća' || parts[0].toLowerCase() === 'namjenska preduzeca' || parts[0].toLowerCase() === 'istorija' || parts[0].toLowerCase() === 'vijesti' || parts[0].toLowerCase() === 'vojna istorija'))
                            ? []
                            : ((parts[0].toLowerCase() === 'naoružanje' || parts[0].toLowerCase() === 'municija' || parts[0].toLowerCase() === 'analitika' || parts[0].toLowerCase() === 'namjenska preduzeća' || parts[0].toLowerCase() === 'namjenska preduzeca' || parts[0].toLowerCase() === 'istorija' || parts[0].toLowerCase() === 'vijesti' || parts[0].toLowerCase() === 'vojna istorija') ? parts.slice(1) : parts);

                        return showParts.length > 0 ? (
                            <p className="text-sm sm:text-base text-white font-medium mb-4">
                                {showParts.map((part, index, arr) => (
                                    <span key={index}>
                                        {translateCat(part)}
                                        {index < arr.length - 1 && (
                                            <span className="text-gray-400 mx-3">›</span>
                                        )}
                                    </span>
                                ))}
                            </p>
                        ) : null;
                    })()}

                    <h1
                        className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 text-white"
                        style={{ fontFamily: 'Armalite Rifle, sans-serif' }}
                    >
                        {translateCat(title)}
                    </h1>

                    {/* Add quick links for supported categories */}
                    {(!podkategorija && !podpodkategorija && quickItems.length > 0) && (
                        <>
                            <div className="mt-4 flex flex-wrap justify-center gap-3">
                                {quickItems.map((sub) => (
                                    <button
                                        key={sub}
                                        type="button"
                                        onClick={() => handlePodClick(sub)}
                                        className={`px-3 py-1 rounded-full text-sm ${activePodkategorija === sub ? 'bg-blue-600 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
                                    >
                                        {translateCat(sub)}
                                    </button>
                                ))}
                            </div>

                            {activePodkategorija && podpodkategorijeForActive.length > 0 && (
                                <div className={podpodContainerClass}>
                                    {podpodkategorijeForActive.map(pp => (
                                        <button
                                            key={pp}
                                            type="button"
                                            onClick={() => handlePodpodClick(pp)}
                                            className={`px-2 py-1 rounded-full text-sm ${activePodpodkategorija === pp ? 'bg-blue-600 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
                                        >
                                            {translateCat(pp)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>


            <div className={`${noHeaderPadding ? 'w-full' : 'px-[20px] md:px-0 w-full'}`}>

                {/* Latest News Section */}
                <section className="w-full py-16 flex-1 md:px-[calc(1rem+180px)]">
                    {/* Section Header (full-bleed on mobile) */}
                    <div className="mb-6 w-full">
                        {/* When `noHeaderPadding` is active we render the blue background on an inner box
                            that uses the same horizontal sizing (`px-[20px] md:px-0`) as the first article,
                            so the blue bar's length matches the article below on mobile. */}
                        {noHeaderPadding ? (
                            <div className="w-full flex">
                                <div className="w-full px-[20px] md:px-0">
                                    <div
                                        className={`w-full ${headerInnerClass}`}
                                        style={{ backgroundColor: '#111827', borderRadius: '5px' }}
                                    >
                                        <div className="w-full text-left">
                                            <span className="text-white font-bold text-base sm:text-lg md:text-xl uppercase tracking-tight px-[10px]">
                                                ★&nbsp;&nbsp;&nbsp;{headerTitle}
                                            </span>
                                            {displayedArticles.length === 0 && subcategories.length > 0 && (
                                                <div className="md:hidden mt-2 px-[10px]">
                                                    <p className="text-white text-sm mb-1">{siteLang === 'en' ? 'Subcategories:' : 'Podkategorije:'}</p>
                                                    <ul className="text-white text-sm list-disc list-inside">
                                                        {subcategories.map(sub => (
                                                            <li key={sub}>{translateCat(sub)}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                className={`w-full ${headerInnerClass}`}
                                style={{ backgroundColor: '#111827', borderRadius: '5px' }}
                            >
                                <div className="w-full text-left">
                                    <span className="text-white font-bold text-base sm:text-lg md:text-xl uppercase tracking-tight px-[10px]">
                                        ★&nbsp;&nbsp;&nbsp;{headerTitle}
                                    </span>
                                    {displayedArticles.length === 0 && subcategories.length > 0 && (
                                        <div className="md:hidden mt-2 px-[10px]">
                                            <p className="text-white text-sm mb-1">{siteLang === 'en' ? 'Subcategories:' : 'Podkategorije:'}</p>
                                            <ul className="text-white text-sm list-disc list-inside">
                                                {subcategories.map(sub => (
                                                    <li key={sub}>{translateCat(sub)}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* News Grid */}
                    {loading ? (
                        <div className="text-center py-12">
                            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>{siteLang === 'en' ? 'Loading articles...' : 'Učitavanje članaka...'}</p>
                        </div>
                    ) : displayedArticles.length === 0 ? (
                        // Make empty state expand so footer stays at bottom
                        <div className="flex-1 flex items-center justify-center">
                            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>{siteLang === 'en' ? 'No articles published' : 'Nema objavljenih članaka'}</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {displayedArticles.map((article, idx) => (
                                <Link
                                    key={article.id}
                                    href={getArticleUrl(article)}
                                    className={`group ${noHeaderPadding && idx === 0 ? 'px-[20px] md:px-0' : ''}`}
                                >
                                    <div
                                        className={`${darkMode ? 'bg-gray-800' : 'bg-white'} overflow-hidden shadow-md hover:shadow-lg transition-shadow`}
                                        style={{ borderRadius: '5px' }}
                                    >
                                        {/* Image */}
                                        {article.naslovna_slika && (
                                            <div
                                                className={`relative overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                                                style={{ height: '300px' }}
                                            >
                                                <img
                                                    src={article.naslovna_slika}
                                                    alt={article.naslov}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                                />
                                            </div>
                                        )}

                                        {/* Content */}
                                        <div className="px-0 py-3">
                                            {/* Label logic:
                                                - On 'Vijesti' show main category
                                                - On top-level 'Naoružanje' show "podkategorija › podpodkategorija" when available
                                                - Otherwise prefer podpodkategorija then podkategorija then kategorija */}
                                            {(() => {
                                                const rawLabel = kategorija === 'Vijesti'
                                                    ? article.kategorija
                                                    : kategorija === 'Naoružanje'
                                                        ? (article.podkategorija
                                                            ? article.podkategorija + (article.podpodkategorija ? ' › ' + article.podpodkategorija : '')
                                                            : (article.podpodkategorija || article.kategorija))
                                                        : (article.podpodkategorija || article.podkategorija || article.kategorija);

                                                const label = rawLabel
                                                    ? rawLabel.split(' › ').map(p => {
                                                        const renamed = displayRenames[p] ?? p;
                                                        return siteLang === 'en' ? (categoryTranslations[renamed] ?? categoryTranslations[p] ?? renamed) : renamed;
                                                    }).join(' › ')
                                                    : rawLabel;

                                                return label ? (
                                                    <span className={`text-xs font-bold ${darkMode ? 'text-gray-400' : 'text-gray-700'} uppercase tracking-wide`}>
                                                        {label}
                                                    </span>
                                                ) : null;
                                            })()}
                                            <h3
                                                className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mt-2 mb-1 line-clamp-3 group-hover:text-blue-600`}
                                                style={{ lineHeight: '1.2' }}
                                            >
                                                {article.naslov}
                                            </h3>
                                            <p className={`text-base font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-2 mb-4`}>
                                                {article.kratak_opis}
                                            </p>
                                            <div className={`text-[10px] font-bold ${darkMode ? 'text-gray-500' : 'text-gray-500'} tracking-wide`}>
                                                {(`${article.autor} / ${article.datum}`).charAt(0).toUpperCase() + (`${article.autor} / ${article.datum}`).slice(1).toLowerCase()}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>

                {/* Footer */}
            </div>
            <div className="mt-auto">
                <Footer />
            </div>
        </main >
    );
}
